import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import { getIdToken } from '../firebase/getIdToken';

/**
 * メール配信ページ (ブラストメール内製化) のデータ層。
 *
 * バックエンドは Cloud Run hozenall-api の mail_* エンドポイント
 * (functions-python/handlers/mail_delivery_handler.py)。設計正本は docs/メール配信ページ_設計提案.md。
 *
 *   useMailMasters        送信元・配信リスト・テンプレート (画面の初期表示用)
 *   useMailCampaignList   配信履歴の一覧 (ページング・状態・件名検索)
 *   useMailCampaign       配信 1 件 (詳細ページ。送信中は 10 秒ごとに再取得)
 *   useMailDeliveries     配信 1 件の宛先一覧 (ページング・状態・検索)
 *   mailApi               保存・複製・削除・プレビュー・件数集計・テスト送信・配信開始・予約取消
 *   uploadMailImage       本文に載せる画像のアップロード (公開バケットへ置き URL を返す)
 */

// ---------------------------------------------------------------- 型
export type MailBlockType = 'text' | 'image' | 'line' | 'button' | 'html';

/** 本文を構成するブロック (services/mail_block_renderer.py の render_block と同じキー) */
export type MailBlock = {
  type: MailBlockType;
  // text
  text?: string;
  font_size?: number;
  bold?: boolean;
  color?: string;
  align?: 'left' | 'center' | 'right';
  // image
  url?: string;
  alt?: string;
  width?: number;
  link?: string;
  // line
  thickness?: number;
  // button
  label?: string;
  text_color?: string;
  // html
  html?: string;
};

export type MailFromAddress = {
  id: number;
  email: string;
  display_name: string | null;
  reply_to: string | null;
  is_default: boolean;
  is_active: boolean;
};

export type MailList = {
  list_code: string;
  list_name: string;
  description: string | null;
  is_active: boolean;
  active_count: number;
  total_count: number;
};

export type MailTemplate = {
  id: number;
  name: string;
  subject: string | null;
  from_address_id: number | null;
  blocks_json: MailBlock[];
  body_text: string | null;
  is_active: boolean;
  note: string | null;
};

export type MailCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'completed' | 'cancelled' | 'failed';

/** 配信履歴一覧の 1 行 (mail_campaign_list) */
export type MailCampaignRow = {
  id: number;
  subject: string;
  status: MailCampaignStatus;
  list_codes: string[] | null;
  list_names: string | null;
  from_email: string | null;
  from_display_name: string | null;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  total_count: number;
  sent_count: number;
  delivered_count: number;
  bounce_count: number;
  complaint_count: number;
  failed_count: number;
  open_count: number;
  click_count: number;
  legacy_no: number | null;
  created_at: string;
  created_operator_id: string | null;
};

/** 配信 1 件 (mail_campaign_get) */
export type MailCampaign = MailCampaignRow & {
  from_address_id: number;
  reply_to: string | null;
  template_id: number | null;
  copied_from_id: number | null;
  blocks_json: MailBlock[] | null;
  body_html: string | null;
  body_text: string | null;
  filter_json: Record<string, any> | null;
  suppressed_count: number;
  ses_configuration_set: string | null;
  error_message: string | null;
  updated_at: string;
};

/** 配信の保存値 (下書き・予約のみ更新可) */
export type MailCampaignInput = {
  id?: number;
  subject: string;
  from_address_id: number | null;
  reply_to: string | null;
  template_id: number | null;
  copied_from_id?: number | null;
  blocks_json: MailBlock[];
  body_text: string | null;
  list_codes: string[];
  filter_json: Record<string, any> | null;
  scheduled_at: string | null;
};

export type MailDeliveryStatus = 'queued' | 'sent' | 'delivered' | 'bounced' | 'complained' | 'failed' | 'suppressed';

/** 宛先 1 件 (mail_campaign_deliveries) */
export type MailDeliveryRow = {
  id: number;
  customer_id: string;
  subscription_id: number | null;
  email: string;
  recipient_name: string | null;
  customer_name: string | null;
  status: MailDeliveryStatus;
  bounce_type: string | null;
  bounce_sub_type: string | null;
  diagnostic: string | null;
  error_message: string | null;
  click_count: number;
  sent_at: string | null;
  delivered_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  opened_at: string | null;
  first_clicked_at: string | null;
};

export type SubscriptionStatus = 'active' | 'stopped' | 'bounced' | 'unsubscribed';

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  active: '配信中',
  stopped: '停止',
  bounced: 'エラー停止',
  unsubscribed: '解除',
};

/** 配信リストの読者 1 件 (mail_list_members) */
export type MailListMember = {
  subscription_id: number;
  customer_id: string;
  customer_type: 'corporate' | 'individual';
  customer_name: string | null;
  email: string;
  recipient_name: string | null;
  recipient_branch: string | null;
  status: SubscriptionStatus;
  bounce_count: number;
  source: string | null;
  updated_at: string | null;
  suppression_reason: string | null;
  list_names: string | null;
};

export type MemberUpdateInput = {
  subscription_id: number;
  action: 'stop' | 'resume' | 'remove' | 'edit';
  list_code?: string;
  email?: string;
  recipient_name?: string | null;
  recipient_branch?: string | null;
};

export type MemberAddInput = {
  list_code: string;
  email: string;
  recipient_name: string | null;
  recipient_branch: string | null;
  customer_id?: string | null;
  new_corporate?: { company_name: string; customer_branch_name?: string; tel_number?: string; address_main?: string } | null;
};

/** CRM 顧客検索の 1 件 (search_crm_customers) */
export type CustomerHit = { id: string; customer_type: 'corporate' | 'individual'; display_name: string | null; employer_name: string | null };

export type RecipientCount = {
  count: number;
  suppressed: number;
  sample: { email: string; recipient_name: string | null; company_name: string | null }[];
};

export const CAMPAIGN_STATUS_LABEL: Record<MailCampaignStatus, string> = {
  draft: '下書き',
  scheduled: '予約',
  sending: '送信中',
  completed: '完了',
  cancelled: '中止',
  failed: '失敗',
};

export const DELIVERY_STATUS_LABEL: Record<MailDeliveryStatus, string> = {
  queued: '待機',
  sent: '送信済',
  delivered: '配信成功',
  bounced: 'バウンス',
  complained: '苦情',
  failed: '失敗',
  suppressed: '除外',
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

// サーバーの理由 (400 の message / 500 の error) をそのまま画面へ返す
function errorMessage(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;
}

async function post<T = any>(name: string, body: Record<string, any>): Promise<ApiResult<T>> {
  try {
    const res = await axios.post(ep(name), body);
    if (res.data?.status === 'success') return { ok: true, data: res.data as T };
    return { ok: false, error: res.data?.message || res.data?.error || '処理に失敗しました。' };
  } catch (e: any) {
    console.error(`[mail] ${name} の呼び出しエラー:`, e);
    return { ok: false, error: errorMessage(e, '処理に失敗しました。') };
  }
}

// ---------------------------------------------------------------- API (フック外から呼ぶ操作)
export const mailApi = {
  saveCampaign: (data: MailCampaignInput, operator: string) =>
    post<{ id: number }>('mail_campaign_save', { ...data, operator_id: operator }),
  copyCampaign: (id: number, operator: string) =>
    post<{ id: number }>('mail_campaign_copy', { id, operator_id: operator }),
  deleteDraft: (id: number) => post('mail_campaign_delete', { id }),
  preview: (blocks: MailBlock[], bodyText: string | null) =>
    post<{ html: string; text: string }>('mail_campaign_preview', { blocks_json: blocks, body_text: bodyText }),
  countRecipients: (listCodes: string[], filter: Record<string, any> | null) =>
    post<RecipientCount>('mail_campaign_recipients', { list_codes: listCodes, filter_json: filter }),
  sendTest: (data: MailCampaignInput, to: string[]) =>
    post<{ sent: number; message_ids: string[] }>('mail_campaign_send_test', { ...data, to }),
  startCampaign: (id: number, operator: string, scheduledAt: string | null) =>
    post<{ campaign_id: number; campaign_status: MailCampaignStatus; total: number; scheduled_at: string | null }>(
      'mail_campaign_start', { id, operator_id: operator, scheduled_at: scheduledAt },
    ),
  cancelCampaign: (id: number, operator: string) => post('mail_campaign_cancel', { id, operator_id: operator }),
  saveTemplate: (data: Partial<MailTemplate> & { name: string; blocks_json: MailBlock[] }, operator: string) =>
    post<{ id: number }>('mail_template_save', { ...data, operator_id: operator }),
  listMembers: (listCode: string, page: number, pageSize: number, q: string, status: string) =>
    post<{ rowsResult: MailListMember[]; total: number; status_counts: Record<string, number> }>(
      'mail_list_members', { list_code: listCode, page, pageSize, q, status },
    ),
  updateMember: (data: MemberUpdateInput, operator: string) => post('mail_list_member_update', { ...data, operator_id: operator }),
  addMember: (data: MemberAddInput, operator: string) =>
    post<{ subscription_id: number; customer_id: string; created_customer: boolean; warnings: string[] }>(
      'mail_list_member_add', { ...data, operator_id: operator },
    ),
  searchCustomers: (query: string) =>
    post<{ results: CustomerHit[] }>('search_crm_customers', { query }),
};

// ---------------------------------------------------------------- 画像アップロード
export type MailImageUploadResult = {
  url: string;
  width: number;
  height: number;
  bytes: number;
  original_bytes: number;
  content_type: string;
};

/** 受け付ける元ファイルの上限 (サーバー側 mail_asset_service.MAX_UPLOAD_BYTES と同じ) */
export const MAIL_IMAGE_MAX_BYTES = 20 * 1024 * 1024;
/** これより大きい写真は送る前にブラウザで縮小する (送信量を抑える。最終の縮小・EXIF 除去はサーバー側) */
const PRESHRINK_BYTES = 4 * 1024 * 1024;
const PRESHRINK_WIDTH = 2400;

/** Google Drive / Docs の共有リンクか (閲覧ページの URL で、メールの画像としては表示できない) */
export function isDriveLink(url: string | null | undefined): boolean {
  const u = (url || '').toLowerCase();
  return u.includes('drive.google.com/') || u.includes('docs.google.com/');
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error || new Error('ファイルを読み込めませんでした'));
    r.readAsDataURL(blob);
  });
}

/** 大きな写真をブラウザで縮小する (GIF はアニメーションを保つためそのまま)。失敗時は元ファイルを返す */
async function preshrink(file: File): Promise<Blob> {
  if (file.type === 'image/gif' || file.size <= PRESHRINK_BYTES || typeof createImageBitmap !== 'function') return file;
  try {
    const bmp = await createImageBitmap(file); // 写真の向き (EXIF) は反映された状態で読まれる
    const scale = Math.min(1, PRESHRINK_WIDTH / bmp.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

/** 画像をアップロードし、メールに載せる公開 URL を返す (社内アカウントの ID トークン必須) */
export async function uploadMailImage(file: File, campaignId: number | null | undefined): Promise<ApiResult<MailImageUploadResult>> {
  if (!file.type.startsWith('image/')) return { ok: false, error: '画像ファイル (JPEG / PNG / GIF / WebP) を選んでください。' };
  if (file.size > MAIL_IMAGE_MAX_BYTES) return { ok: false, error: '画像が大きすぎます (20MB まで)。' };
  try {
    const token = await getIdToken();
    if (!token) return { ok: false, error: 'ログイン情報を確認できませんでした。再読み込みしてください。' };
    const content = await blobToBase64(await preshrink(file));
    const res = await axios.post(
      ep('mail_asset_upload'),
      { content_base64: content, filename: file.name, campaign_id: campaignId ?? null },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.data?.status === 'success') return { ok: true, data: res.data as MailImageUploadResult };
    return { ok: false, error: res.data?.message || res.data?.error || 'アップロードに失敗しました。' };
  } catch (e: any) {
    console.error('[mail] mail_asset_upload の呼び出しエラー:', e);
    return { ok: false, error: errorMessage(e, 'アップロードに失敗しました。') };
  }
}

// ---------------------------------------------------------------- マスタ
export function useMailMasters() {
  const [fromAddresses, setFromAddresses] = useState<MailFromAddress[]>([]);
  const [lists, setLists] = useState<MailList[]>([]);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const r = await post<{ from_addresses: MailFromAddress[]; lists: MailList[]; templates: MailTemplate[] }>('mail_masters', {});
    if (r.ok) {
      setFromAddresses(r.data.from_addresses || []);
      setLists(r.data.lists || []);
      setTemplates(r.data.templates || []);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { fromAddresses, lists, templates, loading, error, reload };
}

// ---------------------------------------------------------------- 配信履歴一覧
export function useMailCampaignList() {
  const [rows, setRows] = useState<MailCampaignRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<MailCampaignStatus | ''>('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const r = await post<{ rowsResult: MailCampaignRow[]; total: number }>('mail_campaign_list', { page, pageSize, status, q });
    if (r.ok) {
      setRows(r.data.rowsResult || []);
      setTotal(r.data.total || 0);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [page, pageSize, status, q]);

  useEffect(() => {
    reload();
  }, [reload]);

  // 検索条件の変更は 1 ページ目へ戻す
  const changeStatus = useCallback((s: MailCampaignStatus | '') => {
    setStatus(s);
    setPage(0);
  }, []);
  const changeQuery = useCallback((s: string) => {
    setQ(s);
    setPage(0);
  }, []);
  const changePageSize = useCallback((n: number) => {
    setPageSize(n);
    setPage(0);
  }, []);

  return { rows, total, page, pageSize, status, q, loading, error, reload, setPage, changePageSize, changeStatus, changeQuery };
}

// ---------------------------------------------------------------- 配信 1 件
export function useMailCampaign(id: number | null) {
  const [campaign, setCampaign] = useState<MailCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (id == null || Number.isNaN(id)) {
      setCampaign(null);
      setLoading(false);
      return;
    }
    const r = await post<{ data: MailCampaign }>('mail_campaign_get', { id });
    if (r.ok) {
      setCampaign(r.data.data);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  // 送信中は 10 秒間隔で進捗を取り直す
  const statusRef = useRef<MailCampaignStatus | null>(null);
  statusRef.current = campaign?.status ?? null;
  useEffect(() => {
    if (campaign?.status !== 'sending') return;
    const t = window.setInterval(() => {
      if (statusRef.current === 'sending') reload();
    }, 10000);
    return () => window.clearInterval(t);
  }, [campaign?.status, reload]);

  return { campaign, loading, error, reload };
}

// ---------------------------------------------------------------- 宛先一覧
export function useMailDeliveries(campaignId: number | null, enabled: boolean) {
  const [rows, setRows] = useState<MailDeliveryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [status, setStatus] = useState<MailDeliveryStatus | ''>('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled || campaignId == null) return;
    setLoading(true);
    const r = await post<{ rowsResult: MailDeliveryRow[]; total: number; status_counts: Record<string, number> }>(
      'mail_campaign_deliveries', { id: campaignId, page, pageSize, status, q },
    );
    if (r.ok) {
      setRows(r.data.rowsResult || []);
      setTotal(r.data.total || 0);
      setStatusCounts(r.data.status_counts || {});
    }
    setLoading(false);
  }, [enabled, campaignId, page, pageSize, status, q]);

  useEffect(() => {
    reload();
  }, [reload]);

  const changeStatus = useCallback((s: MailDeliveryStatus | '') => {
    setStatus(s);
    setPage(0);
  }, []);
  const changeQuery = useCallback((s: string) => {
    setQ(s);
    setPage(0);
  }, []);
  const changePageSize = useCallback((n: number) => {
    setPageSize(n);
    setPage(0);
  }, []);

  return { rows, total, statusCounts, page, pageSize, status, q, loading, reload, setPage, changePageSize, changeStatus, changeQuery };
}

// ---------------------------------------------------------------- 表示用ユーティリティ
/** ISO 文字列 (JST オフセット付き) を "YYYY/MM/DD HH:mm" にする */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 件数と率 ("1,234 (63.5%)") */
export function fmtCountRate(n: number | null | undefined, base: number | null | undefined): string {
  const v = n ?? 0;
  if (!base) return v.toLocaleString();
  return `${v.toLocaleString()} (${((v / base) * 100).toFixed(1)}%)`;
}

/** 空ブロック (種別ごとの既定値) */
export function newBlock(type: MailBlockType): MailBlock {
  switch (type) {
    case 'text':
      return { type, text: '', font_size: 14, bold: false, color: '#333333', align: 'left' };
    case 'image':
      return { type, url: '', alt: '', width: 600, link: '', align: 'center' };
    case 'line':
      return { type, color: '#cccccc', thickness: 1 };
    case 'button':
      return { type, label: '詳しくはこちら', url: '', color: '#007F40', text_color: '#ffffff', align: 'center' };
    case 'html':
    default:
      return { type: 'html', html: '' };
  }
}
